import React, { useMemo } from "react";
import TodayRow from "./TodayRow";
import AvatarChip from "./ui/avatar-chip";

/**
 * MyBriefsSection — always-visible list of user's open briefs, sorted by pitch date.
 * Hidden entirely if the user has zero open briefs.
 *
 * Props:
 *   briefs [{ brief_id, title, status, company_name, pitch_date, assignee_name, ... }]
 *   onNavigate(path)
 *   todayStr  YYYY-MM-DD
 *   onSeeAll?  — optional "See all briefs →" link handler
 */
const OPEN_STATUSES = new Set(["New", "Assigned", "In Progress", "Pitched"]);

export default function MyBriefsSection({ briefs = [], onNavigate, todayStr, onSeeAll }) {
  const { openBriefs, dueSoonCount } = useMemo(() => {
    const open = (briefs || [])
      .filter((b) => b && b.brief_id)
      .filter((b) => !b.status || OPEN_STATUSES.has(b.status))
      .sort((a, b) => {
        // Sort by pitch date ascending (nulls last)
        if (a.pitch_date && b.pitch_date) return a.pitch_date.localeCompare(b.pitch_date);
        if (a.pitch_date) return -1;
        if (b.pitch_date) return 1;
        return 0;
      });
    // "Due soon" = pitch date within 3 days
    const due = open.filter((b) => {
      if (!b.pitch_date) return false;
      const d = b.pitch_date.slice(0, 10);
      return d >= todayStr && daysFromNow(d, todayStr) <= 3;
    });
    return { openBriefs: open, dueSoonCount: due.length };
  }, [briefs, todayStr]);

  if (openBriefs.length === 0) return null;

  return (
    <div style={{ marginTop: 36 }}>
      <SectionHead
        title="My briefs"
        count={`${openBriefs.length} active`}
        badge={dueSoonCount > 0 ? `${dueSoonCount} due soon` : null}
        onSeeAll={onSeeAll}
      />
      <div>
        {openBriefs.map((b) => (
          <TodayRow
            key={b.brief_id}
            kind="brief"
            text={b.title || b.company_name || "Untitled brief"}
            onClick={() => onNavigate?.(`/bizdev/briefs/${b.brief_id}`)}
            metadata={
              <>
                {b.status && <TodayRow.Status status={b.status} />}
                {b.company_name && <><TodayRow.Sep /><span>{b.company_name}</span></>}
                {b.assignee_name && <><TodayRow.Sep /><AvatarChip name={b.assignee_name} /></>}
              </>
            }
            rightBadge={formatPitchDate(b.pitch_date, todayStr)}
          />
        ))}
      </div>
    </div>
  );
}

function SectionHead({ title, count, badge, onSeeAll }) {
  return (
    <div style={{
      display: "flex",
      alignItems: "center",
      gap: 12,
      marginBottom: 16,
      padding: "0 4px",
      userSelect: "none",
    }}>
      <span style={{
        fontFamily: "var(--font-sans)",
        fontSize: 19,
        fontWeight: 600,
        color: "var(--t1)",
        letterSpacing: "-0.015em",
      }}>
        {title}
      </span>
      {count != null && (
        <span style={{ color: "var(--t3)", fontSize: 13, fontWeight: 500 }}>{count}</span>
      )}
      {badge && (
        <span style={{
          fontSize: 11,
          fontWeight: 600,
          padding: "3px 9px",
          borderRadius: 999,
          background: "rgba(245,158,11,.14)",
          color: "var(--a)",
        }}>{badge}</span>
      )}
      <span style={{ flex: 1 }} />
      {onSeeAll && (
        <button
          type="button"
          onClick={onSeeAll}
          style={{
            fontSize: 13,
            color: "var(--t3)",
            background: "transparent",
            border: "none",
            cursor: "pointer",
            padding: 0,
            fontFamily: "var(--font-sans)",
            transition: "color .15s",
          }}
          onMouseEnter={(e) => e.currentTarget.style.color = "#60a5fa"}
          onMouseLeave={(e) => e.currentTarget.style.color = "var(--t3)"}
        >
          See all briefs →
        </button>
      )}
    </div>
  );
}

function daysFromNow(dateStr, todayStr) {
  try {
    const d = new Date(dateStr + "T00:00:00");
    const t = new Date(todayStr + "T00:00:00");
    return Math.round((d - t) / 86400000);
  } catch { return 99; }
}

function formatPitchDate(dateStr, todayStr) {
  if (!dateStr) return null;
  const diff = daysFromNow(dateStr.slice(0, 10), todayStr);
  if (diff < 0) return <span style={{ color: "var(--r)" }}>Overdue</span>;
  if (diff === 0) return <span style={{ color: "var(--a)", fontWeight: 600 }}>Pitch today</span>;
  if (diff === 1) return <span style={{ color: "var(--a)", fontWeight: 600 }}>Pitch tomorrow</span>;
  if (diff <= 3) return <span style={{ color: "var(--a)" }}>Pitch in {diff}d</span>;
  try {
    const d = new Date(dateStr.slice(0, 10) + "T00:00:00");
    return d.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
  } catch { return dateStr; }
}
